import { citedNumbers } from "@/shared/components/markdown/Markdown";
import type { Message, TurnPart, WebSource } from "@/shared/types";

type Sources = ReadonlyMap<number, WebSource>;

/** The sources a text cites, by number: as it draws, so code and links don't count. */
export const citedSources = (text: string, sources: Sources): WebSource[] =>
  sources.size ? citedNumbers(text).flatMap((n) => sources.get(n) ?? []).sort((a, b) => a.n - b.n) : [];

/** The answer's words and, when it cites any of `sources`, the list of them by number. */
export function withSourceList(text: string, sources: Sources): string {
  const cited = citedSources(text, sources);
  if (!cited.length) return text;
  return `${text}\n\nSources:\n${cited.map((source) => `[${source.n}] ${source.title} – ${source.url}`).join("\n")}`;
}

/** The web sources a turn's calls brought back. */
export const sourcesOf = (parts: TurnPart[]): WebSource[] =>
  parts.flatMap((part) => (part.kind === "tool" ? part.step.web?.sources ?? [] : []));

/** Each agent turn's citable sources, by number: its own and those of every turn above it. A
    later turn's number wins: numbers start over only past what the agent's summary dropped.
    A turn whose sources are what they were in `before` keeps that same map, so a memoised
    turn isn't drawn again for every word another turn streams. */
export function chatSources(messages: Message[], before: Map<string, Sources> = new Map()): Map<string, Sources> {
  const byTurn = new Map<string, Sources>();
  let known: Sources = new Map();
  for (const m of messages) {
    const found = m.parts ? sourcesOf(m.parts) : [];
    if (found.length) known = new Map([...known, ...found.map((source) => [source.n, source] as const)]);
    if (m.role !== "agent") continue;
    const previous = before.get(m.id);
    byTurn.set(m.id, previous && sameSources(previous, known) ? previous : known);
  }
  return byTurn;
}

const sameSources = (a: Sources, b: Sources) =>
  a.size === b.size && [...b].every(([n, source]) => a.get(n)?.url === source.url);
